import { difference, find, flatMap, isEmpty, uniq } from "lodash";
import { useCallback } from "react";
import useSWR from "swr";
import { ANY_LOCATION_ID, PERMISSIONS } from "../constants";
import { LocationPermissions, Permissions } from "../models";
import fetcher from "./fetcher";

const usePermissions = () => {
    const { data, isLoading, mutate } = useSWR(
        `/api/permission-management/v1/users/me/permissions`,
        fetcher,
        {
            fallbackData: {
                globalPermissions: [],
                companyPermissions: [],
            },
        }
    );
    const ownPermissions = data as Permissions;
    const locationPermissions: LocationPermissions[] = flatMap(
        ownPermissions.companyPermissions,
        "locationPermissions"
    );

    const getLocationsPermissions = useCallback(
        (companyId: string) =>
            find(ownPermissions.companyPermissions, { id: companyId })
                ?.locationPermissions || [],
        [ownPermissions.companyPermissions]
    );

    const getCompanyPermissions = useCallback(
        (companyId?: string) =>
            find(ownPermissions.companyPermissions, { id: companyId })
                ?.permissions || [],
        [ownPermissions.companyPermissions]
    );

    const hasCompanyInPermissions = useCallback(
        (companyId?: string) =>
            !!find(ownPermissions.companyPermissions, { id: companyId }),
        [ownPermissions.companyPermissions]
    );

    const hasLocationInPermissions = useCallback(
        (companyId?: string, locationId?: string) => {
            if (!companyId || !locationId) {
                return false;
            }
            const locations = getLocationsPermissions(companyId);
            return !!find(locations, { id: locationId });
        },
        [getLocationsPermissions]
    );

    // only the betterspace admin has global permissions
    const hasGlobalPermissions = useCallback(
        (permissions: string[] = []) => {
            if (
                !isEmpty(ownPermissions.globalPermissions) &&
                !isEmpty(permissions)
            ) {
                return (
                    difference(permissions, ownPermissions.globalPermissions)
                        .length === 0
                );
            }
            return false;
        },
        [ownPermissions.globalPermissions]
    );

    const hasCompanyPermissions = useCallback(
        (permissions: string[] = [], companyId: string): boolean => {
            if (
                isEmpty(ownPermissions.companyPermissions) ||
                isEmpty(permissions)
            ) {
                return false;
            }

            const companyPermissions =
                find(ownPermissions.companyPermissions, { id: companyId })
                    ?.permissions || [];

            return difference(permissions, companyPermissions).length === 0;
        },
        [ownPermissions.companyPermissions]
    );

    const hasLocationPermissions = useCallback(
        (
            permissions: string[] = [],
            companyId: string,
            locationId: string
        ): boolean => {
            const locations = getLocationsPermissions(companyId);

            if (isEmpty(locations) || isEmpty(permissions)) {
                return false;
            }

            const locationPermissions =
                locationId === ANY_LOCATION_ID
                    ? uniq(flatMap(locations, "permissions"))
                    : find(locations, { id: locationId })?.permissions || [];

            return difference(permissions, locationPermissions).length === 0;
        },
        [getLocationsPermissions]
    );

    const hasPermissions = useCallback(
        ({
            globalPermissions = [PERMISSIONS.BETTERSPACE_ADMIN.READ],
            permissions = [],
            companyId,
            locationId,
        }: {
            globalPermissions?: string[];
            permissions?: string[];
            companyId?: string;
            locationId?: string;
        } = {}) => {
            return (
                hasGlobalPermissions(globalPermissions) ||
                (!!companyId &&
                    hasCompanyPermissions(permissions, companyId)) ||
                (!!companyId &&
                    !!locationId &&
                    hasLocationPermissions(permissions, companyId, locationId))
            );
        },
        [hasCompanyPermissions, hasGlobalPermissions, hasLocationPermissions]
    );

    return {
        isLoadingPermissions: isLoading,
        permissions: ownPermissions,
        companyPermissions: ownPermissions.companyPermissions,
        locationPermissions,
        getLocationsPermissions,
        getCompanyPermissions,
        hasPermissions,
        mutatePermissions: mutate,
        hasLocationInPermissions,
        hasCompanyInPermissions,
    };
};

export default usePermissions;
